import React, { useState, createContext, useContext } from "react";
import tinycolor from "tinycolor2";
import { useSelector, useDispatch } from "react-redux";
import { setTheme } from "../../store/themeSlice";
import axiosInstance from "../../services/axiosInstance";
import { authStorage } from "../../utils/authStorage";

// Create theme context for global color management
const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const themes = [
    { id: 'brand-green', name: 'Brand Green Dark', color: '#14532d' },
    { id: 'navy', name: 'Primary Navy', color: '#001F3F' },
    { id: 'amber', name: 'Amber', color: '#ba6a36' },
    { id: 'espresso', name: 'Espresso', color: '#261311' },
    { id: 'champagne', name: 'Champagne', color: '#e6c8b7' },
    { id: 'cognac', name: 'Cognac', color: '#c3955b' },
    { id: 'emerald', name: 'Emerald', color: '#1c3934' },
    { id: 'teal', name: 'Teal', color: '#0f766e' },
    { id: 'sea-blue', name: 'Sea Blue', color: '#0369a1' },
    { id: 'ruby', name: 'Ruby', color: '#9f1239' },
];

const DEFAULT_THEME_INDEX = 0;

export function ThemeProvider({ children }) {
    const themeId = useSelector(state => state.theme.themeId);
    const currentUser = useSelector(state => state.auth.user) || authStorage.getUser();
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(true);

    // Set theme from per-user localStorage immediately on mount
    React.useLayoutEffect(() => {
        const user = authStorage.getUser();
        const localTheme = authStorage.getTheme(user?.id) || user?.theme;
        if (localTheme) {
            dispatch(setTheme(localTheme));
        }
    }, [dispatch]);

    React.useEffect(() => {
        const fetchTheme = async () => {
            try {
                const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                if (token) {
                    const response = await axiosInstance.get("/user/theme", {
                        headers: { "Authorization": `Bearer ${token}` }
                    });
                    if (response.data && response.data.theme) {
                        authStorage.setTheme(response.data.theme, currentUser?.id);
                        dispatch(setTheme(response.data.theme));
                    }
                }
            } catch (err) {
                console.warn("Failed to fetch theme from backend:", err);
                const user = authStorage.getUser();
                const localTheme = authStorage.getTheme(user?.id) || user?.theme;
                if (localTheme) {
                    dispatch(setTheme(localTheme));
                }
            } finally {
                setLoading(false);
            }
        };
        fetchTheme();
    }, [dispatch, currentUser?.id]);

    const setThemeId = async (newThemeId) => {
        dispatch(setTheme(newThemeId));
        authStorage.setTheme(newThemeId, currentUser?.id);
        try {
            const userStr = localStorage.getItem("user");
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            if (userStr && token) {
                await axiosInstance.put("/user/theme", { theme: newThemeId }, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
            }
        } catch (err) {
            // Optionally show error toast
        }
    };

    React.useEffect(() => {
        try {
            const selectedTheme = themes.find(t => t.id === themeId) || themes[DEFAULT_THEME_INDEX];
            document.documentElement.style.setProperty('--color-primary', selectedTheme.color);
            //use black text for consistency
            document.documentElement.style.setProperty('--color-text', '#111');
            // Set surface background for customer list/dashboard
            const surfaceColor = '#f7f7f7';
            document.documentElement.style.setProperty('--color-surface', surfaceColor);
            // Button active state only
            const activeColor = tinycolor(selectedTheme.color).darken(16).toString();
            document.documentElement.style.setProperty('--color-active', activeColor);
            localStorage.setItem("theme", selectedTheme.id);
            const userStr = localStorage.getItem("user");
            if (userStr) {
                try {
                    const userObj = JSON.parse(userStr);
                    userObj.theme = selectedTheme.id;
                    authStorage.setTheme(selectedTheme.id, userObj.id);
                    localStorage.setItem("user", JSON.stringify(userObj));
                } catch (e) {
                    console.warn("Failed to update theme in user object:", e);
                }
            }
        } catch (error) {
            console.warn("Failed to apply theme:", error);
        }
    }, [themeId]);

    const currentTheme = themes.find(t => t.id === themeId) || themes[DEFAULT_THEME_INDEX];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen w-screen bg-white">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <ThemeContext.Provider value={{ themeId, setThemeId, themes, currentTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useThemeModal() {
    const [isOpen, setIsOpen] = useState(false);
    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);
    return { isOpen, openModal, closeModal };
}
